import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import getSlug from "speakingurl";
import { Box, Input, Alert, Button } from "@showitinc/showit-wc";
import { initFormatter } from "../utils/PropertyFormatters";
import styles from "./NameSlugInput.module.css";
import { copyToClipboard } from "../utils/AppUtils";

const NameSlugInput = ({
  name,
  slug,
  hideSlug = false,
  diffCheckEnabled = true,
  onAreaFocus,
  onSave,
  autoFocus = false,
  labelPrefix,
  propName,
  onValueChange,
  invalid,
}) => {
  const [nameState, setNameState] = useState({ name, slug });
  const nameInput = useRef(null);

  useEffect(() => {
    if (autoFocus && nameInput) {
      nameInput.current.focus();
      nameInput.current.select();
    }
  }, []);

  // Handle if they type the "Enter" key to save the value
  const _onChange = (propInfo) => {
    let emit = true;
    if (diffCheckEnabled) {
      emit = nameState.name !== name;
    }
    const key = propInfo?.value?.detail?.key;

    if (emit && onSave && key === "Enter" && !invalid) {
      const updatedPropInfo = {
        ...propInfo,
        slug: nameState.slug,
        origSlug: slug,
      };

      onSave(updatedPropInfo);
    }
  };

  const _onValueChange = (propInfo) => {
    const newName = propInfo.value;
    const newSlug = getSlug(propInfo.value);

    setNameState({ name: newName, slug: newSlug });

    if (onValueChange) {
      onValueChange({ name: newName, value: newName, slug: newSlug });
    }
  };

  const { onChange: onInput } = initFormatter(_onValueChange);
  const { onChange } = initFormatter(_onChange);

  const _onAreaClick = (e) => {
    if (onAreaFocus) {
      e.stopPropagation();
      e.preventDefault();
      nameInput.current.blur();
      onAreaFocus();
    }
  };

  const _onSlugCopy = async (key) => {
    await copyToClipboard({
      val: key,
      showToast: true,
    });
  };

  return (
    <>
      <Input
        className={styles.name}
        label={`${labelPrefix} Name`}
        onFocus={_onAreaClick}
        onSIInput={onInput(name, nameState.name)}
        onSIKeydown={onChange(name, nameState.name)}
        value={onAreaFocus ? name : nameState.name}
        ref={nameInput}
        error={invalid}
        reserveContext
      />
      {invalid && (
        <Box my="2">
          <Alert
            weight="bold"
            variant="error"
            message={`Please enter a valid ${labelPrefix.toLowerCase()} name to continue`}
          />
        </Box>
      )}
      {nameState.slug && !hideSlug && (
        <Input
          className={styles.slug}
          label={`${labelPrefix} Slug`}
          readonly
          value={onAreaFocus ? slug : nameState.slug}
          reserveContext
        >
          <Button
            variant="icon"
            iconSize="16"
            iconPrefix="copy16"
            className={styles.copyBtn}
            slot="prefix"
            onClick={() => _onSlugCopy(slug)}
          />
        </Input>
      )}
    </>
  );
};

NameSlugInput.propTypes = {
  name: PropTypes.string,
  slug: PropTypes.string,
};

export default NameSlugInput;
